import * as React from "react"

import backofficeChatService from "@services/backofficeChatService.ts"

import { Channel } from "stream-chat"
import { AppMessage } from "@others/AppMessage.ts"
import { AppFunctions } from "@others/AppFunctions.ts"
import { useTranslation } from "react-i18next"
import { AppStreamChatGenerics } from "@interfaces/IAppStreamChatGenerics.ts"
import { ConversationAiControlMode } from "@interfaces/IConversationAiControlStateApi.ts"
import { IConversationAiControlStateCli } from "@interfaces/IConversationAiControlStateCli.ts"

interface IArgs {
	channel?: Channel<AppStreamChatGenerics>
	enabled?: boolean
}

interface IOutput {
	state?: IConversationAiControlStateCli
	mode?: ConversationAiControlMode
	isLoading: boolean
	isSaving: boolean
	isAiEnabled: boolean
	isPausedBannerVisible: boolean
	refresh: () => Promise<IConversationAiControlStateCli | undefined>
	toggleAiEnabled: (
		nextValue: boolean
	) => Promise<IConversationAiControlStateCli | undefined>
	resumeAi: () => Promise<IConversationAiControlStateCli | undefined>
}

interface ISyncStateOptions {
	setLoading?: boolean
	showError?: boolean
}

const getModeFromValue = (value: boolean): ConversationAiControlMode =>
	value ? "ai_active" : "human_owned"

const getDerivedIsPaused = (
	mode: ConversationAiControlMode,
	pausedUntil: string | null
) => {
	if (mode !== "ai_active" || !pausedUntil) return false

	return (
		!Number.isNaN(Date.parse(pausedUntil)) &&
		AppFunctions.checkIfIsInTheFuture({
			date: pausedUntil,
			zone: "Local",
		})
	)
}

const getChannelId = (channel?: Channel<AppStreamChatGenerics>) => {
	if (!channel) return undefined
	if (channel.id) return channel.id
	if (!channel.cid) return undefined

	const cidParts = channel.cid.split(":")

	return cidParts.length > 1 ? cidParts.slice(1).join(":") : undefined
}

const syncChannelAiControlState = (
	channel: Channel<AppStreamChatGenerics>,
	state: IConversationAiControlStateCli
) => {
	channel.data = {
		...(channel.data || {}),
		ai_control_mode: state.mode,
		ai_paused_until: state.pausedUntil,
	}
}

const getStateFromChannel = (
	channel?: Channel<AppStreamChatGenerics>
): IConversationAiControlStateCli | undefined => {
	if (!channel) return undefined

	const mode = channel.data?.ai_control_mode ?? "ai_active"
	const pausedUntil = channel.data?.ai_paused_until ?? null

	return {
		mode,
		pausedUntil,
		isPaused: getDerivedIsPaused(mode, pausedUntil),
	}
}

const useConversationAiControl = ({
	channel,
	enabled = false,
}: IArgs): IOutput => {
	const { t } = useTranslation()

	const [isLoading, setIsLoading] = React.useState(false)
	const [isSaving, setIsSaving] = React.useState(false)

	const [state, setState] = React.useState<
		IConversationAiControlStateCli | undefined
	>(undefined)

	const mountedRef = React.useRef(true)
	const channelRef = React.useRef(channel)
	const stateRef = React.useRef(state)

	const pauseExpiryTimeoutRef = React.useRef<
		ReturnType<typeof setTimeout> | undefined
	>(undefined)

	React.useEffect(() => {
		mountedRef.current = true

		return () => {
			mountedRef.current = false
		}
	}, [])

	React.useEffect(() => {
		channelRef.current = channel
	}, [channel])

	React.useEffect(() => {
		stateRef.current = state
	}, [state])

	const syncStateFromChannel = React.useCallback(
		(targetChannel?: Channel<AppStreamChatGenerics>) => {
			if (!enabled || !targetChannel) return undefined

			const nextState = getStateFromChannel(targetChannel)

			if (mountedRef.current && channelRef.current === targetChannel) {
				setState(nextState)
			}

			return nextState
		},
		[enabled]
	)

	const watchAndSyncState = React.useCallback(
		async ({
			setLoading = false,
			showError = false,
		}: ISyncStateOptions = {}) => {
			const targetChannel = channelRef.current

			if (!enabled || !targetChannel) return undefined

			if (setLoading && mountedRef.current) {
				setIsLoading(true)
			}

			try {
				await targetChannel.watch()
				return syncStateFromChannel(targetChannel)
			} catch {
				if (showError) {
					void AppMessage.error(
						t("message_chat_ai_control_load_failed")
					)
				}

				return undefined
			} finally {
				if (setLoading && mountedRef.current) {
					setIsLoading(false)
				}
			}
		},
		[enabled, syncStateFromChannel, t]
	)

	const getChannelIdentity = React.useCallback(() => {
		const currentChannel = channelRef.current

		return {
			channelId: getChannelId(currentChannel),
			channelType: currentChannel?.type,
		}
	}, [])

	const refresh = React.useCallback(async () => {
		return watchAndSyncState({
			setLoading: true,
			showError: true,
		})
	}, [watchAndSyncState])

	const updateMode = React.useCallback(
		async (nextMode: ConversationAiControlMode) => {
			const currentChannel = channelRef.current

			if (
				!enabled ||
				!currentChannel ||
				stateRef.current === undefined ||
				isSaving
			) {
				return undefined
			}

			const previousState = stateRef.current
			const { channelId, channelType } = getChannelIdentity()

			if (!channelId || !channelType) {
				void AppMessage.error(
					t("message_chat_ai_control_update_failed")
				)
				return undefined
			}

			const optimisticState: IConversationAiControlStateCli = {
				...previousState,
				mode: nextMode,
				pausedUntil:
					nextMode === "ai_active" ? previousState.pausedUntil : null,
				isPaused: false,
			}

			setIsSaving(true)
			setState(optimisticState)

			try {
				const nextState =
					await backofficeChatService.updateConversationAiControl({
						channelId,
						channelType,
						mode: nextMode,
					})

				syncChannelAiControlState(currentChannel, nextState)

				return syncStateFromChannel(currentChannel)
			} catch {
				if (
					mountedRef.current &&
					channelRef.current === currentChannel
				) {
					setState(previousState)
				}

				void AppMessage.error(
					t("message_chat_ai_control_update_failed")
				)

				return undefined
			} finally {
				if (
					mountedRef.current &&
					channelRef.current === currentChannel
				) {
					setIsSaving(false)
				}
			}
		},
		[enabled, getChannelIdentity, isSaving, syncStateFromChannel, t]
	)

	const toggleAiEnabled = React.useCallback(
		(nextValue: boolean) => updateMode(getModeFromValue(nextValue)),
		[updateMode]
	)

	const resumeAi = React.useCallback(
		() => updateMode("ai_active"),
		[updateMode]
	)

	React.useEffect(() => {
		clearTimeout(pauseExpiryTimeoutRef.current)

		if (!enabled || !channel) {
			setState(undefined)
			setIsLoading(false)
			setIsSaving(false)
			return
		}

		setState(getStateFromChannel(channel))
		setIsSaving(false)

		void watchAndSyncState({
			setLoading: true,
			showError: true,
		})
	}, [channel, enabled, watchAndSyncState])

	React.useEffect(() => {
		clearTimeout(pauseExpiryTimeoutRef.current)

		if (
			!enabled ||
			!state?.isPaused ||
			state.mode !== "ai_active" ||
			!state.pausedUntil
		) {
			return
		}

		const pausedUntilTimestamp = Date.parse(state.pausedUntil)

		if (Number.isNaN(pausedUntilTimestamp)) return

		const delay = pausedUntilTimestamp - Date.now()

		if (delay <= 0) {
			syncStateFromChannel(channel)
			return
		}

		pauseExpiryTimeoutRef.current = setTimeout(() => {
			syncStateFromChannel(channelRef.current)
		}, delay)

		return () => {
			clearTimeout(pauseExpiryTimeoutRef.current)
		}
	}, [channel, enabled, state, syncStateFromChannel])

	React.useEffect(() => {
		if (!enabled || !channel) return

		const handleChannelUpdated = () => {
			syncStateFromChannel(channel)
		}

		channel.on("channel.updated", handleChannelUpdated)

		return () => {
			channel.off("channel.updated", handleChannelUpdated)
		}
	}, [channel, enabled, syncStateFromChannel])

	React.useEffect(() => {
		if (!enabled || !channel || typeof document === "undefined") return

		const handleVisibilityChange = () => {
			if (document.visibilityState !== "visible") return

			void watchAndSyncState()
		}

		document.addEventListener("visibilitychange", handleVisibilityChange)

		return () => {
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange
			)
		}
	}, [channel, enabled, watchAndSyncState])

	return {
		state,
		mode: state?.mode,
		isLoading,
		isSaving,
		isAiEnabled: state?.mode === "ai_active",
		isPausedBannerVisible: state?.mode === "ai_active" && state?.isPaused,
		refresh,
		toggleAiEnabled,
		resumeAi,
	}
}

export default useConversationAiControl
